/**
 * Height of a flow canvas that runs from where it sits on the page down to the
 * bottom of the viewport.
 *
 * Kept as a pure module (no React, no DOM) because `vitest.config.ts` runs in
 * the `node` environment and only matches `.test.ts` files — a `.tsx` component
 * test would silently never run, so the arithmetic that decides how tall the
 * canvas gets lives here where it can actually be covered.
 *
 * The caller supplies a measured viewport-relative top (`getBoundingClientRect().top`),
 * so this never needs to know what sits above the canvas — legend chips, error
 * banners and the frozen-version notice all come and go, and each changes the
 * top rather than requiring its own offset constant here.
 */

/**
 * Floor for the canvas height. Below roughly this, a layered DAG stops showing
 * enough rows to be worth fitting and the controls start colliding with the
 * minimap. A viewport too short to allow it lets the page scroll instead —
 * shrinking the canvas further would trade a scrollbar for an unusable canvas.
 */
export const MIN_FLOW_CANVAS_HEIGHT = 450

/**
 * Breathing room left under the canvas so its border does not sit flush against
 * the bottom edge of the viewport.
 */
export const FLOW_CANVAS_BOTTOM_GUTTER = 16

/**
 * Pixels the canvas should occupy: everything from `canvasTop` to the bottom of
 * the viewport, less a small gutter, never less than `MIN_FLOW_CANVAS_HEIGHT`.
 *
 * Returns the floor for any input that isn't a finite measurement — a canvas
 * measured before layout settles reports 0/NaN, and a usable default beats
 * propagating that into a style attribute.
 */
export function flowCanvasHeight(
  viewportHeight: number,
  canvasTop: number,
  bottomGutter: number = FLOW_CANVAS_BOTTOM_GUTTER,
): number {
  if (!Number.isFinite(viewportHeight) || !Number.isFinite(canvasTop)) {
    return MIN_FLOW_CANVAS_HEIGHT
  }
  const gutter = Number.isFinite(bottomGutter) ? bottomGutter : FLOW_CANVAS_BOTTOM_GUTTER
  const available = viewportHeight - canvasTop - gutter
  return Math.max(MIN_FLOW_CANVAS_HEIGHT, Math.round(available))
}
